import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Typography,
  Card,
  Tabs,
  Modal,
  message,
  Statistic,
  Tag,
  Tooltip,
} from "antd";
import { trackFeatureUsage } from "../lib/posthog";
import styled from "@emotion/styled";
import { AdminNavBar } from "../components/AdminNavBar";
import { supabase } from "../lib/supabase";
import { SupabaseClient } from "@supabase/supabase-js";
import { useLocation } from "react-router-dom";

// Type cast supabase to avoid type errors
const typedSupabase = supabase as unknown as SupabaseClient;

// Import types
import { Booking } from "../types/booking";

// Import components
import BookingsList from "../components/BookingsAdmin/BookingsList";
import BookingDetails from "../components/BookingsAdmin/BookingDetails";

// Import services
import {
  fetchBookingStatusCounts,
  fetchTotalBookingsCount,
  fetchBookingById,
} from "../services/bookingService";

// Import utils
import { generateAndDownloadPackingSlip } from "../services/packingSlipService";
import { validStatuses, statusColors } from "../utils/bookingStatus";
import {
  useTabNotifications,
  playNotificationSound,
  updateTabTitle,
  showBrowserNotification,
} from "../utils/notificationUtils";

// Import custom hooks
import { useBookingUpdates } from "../hooks/useBookingUpdates";

const { Title } = Typography;
const { TabPane } = Tabs;

// Define styled components
const Container = styled.div`
  padding: 24px;
`;

const StatusMetricCard = styled(Card)`
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .ant-statistic-title {
    font-size: 12px;
    margin-bottom: 0;
  }

  .ant-statistic-content {
    font-size: 18px;
  }
`;

const StatusMetricsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const BookingsAdmin: React.FC = () => {
  // State for bookings
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [viewDetailsModalVisible, setViewDetailsModalVisible] = useState(false);

  // State for triggering list refresh on real-time updates
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // State for stats
  const [totalBookingsCount, setTotalBookingsCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<
    { status: string; count: number }[]
  >([]);

  // Reference to track previous pending count for sound notifications
  const prevPendingCountRef = useRef<number>(0);

  // State for active tab
  const [activeTab, setActiveTab] = useState("pending");

  // State for selected status filter
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<
    string | null
  >(null);

  // Add state for resetting filters
  const [resetFilters, setResetFilters] = useState<number | null>(null);

  // Add state to track if there are unsaved changes in the booking details
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Calculate pending approvals count
  const pendingApprovalsCount =
    statusCounts.find((s) => s.status === "submitted")?.count || 0;

  // Get URL query parameters
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const bookingIdParam = searchParams.get("booking_id");
  const tabParam = searchParams.get("tab");

  // IIFE to force title update on component creation for the badge
  // This runs immediately when the component is defined
  (() => {
    // Get stored count if it exists
    const storedCount = localStorage.getItem("pendingApprovalCount");
    const count = storedCount ? parseInt(storedCount) : 0;

    if (count > 0) {
      console.log("🔴 Immediate title update with stored count:", count);
      // Set document title directly
      document.title = `(${count}) Bookings Admin`;

      // Try window method too
      if (window.updateTitleWithCount) {
        window.updateTitleWithCount(count);
      }
    }
  })();

  // Log when pending count changes for debugging
  useEffect(() => {
    console.log(
      `Pending approvals count updated: ${pendingApprovalsCount} (should update badge in tab)`
    );

    // Store the count in localStorage for persistence
    localStorage.setItem(
      "pendingApprovalCount",
      pendingApprovalsCount.toString()
    );

    // Update the title in multiple ways for maximum compatibility
    // 1. Direct document.title update
    if (pendingApprovalsCount > 0) {
      document.title = `(${pendingApprovalsCount}) Bookings Admin`;
    } else {
      document.title = "Bookings Admin";
    }

    // 2. Use the global window method
    if (window.updateTitleWithCount) {
      try {
        window.updateTitleWithCount(pendingApprovalsCount);
      } catch (e) {
        console.error("Error calling window.updateTitleWithCount:", e);
      }
    }

    // 3. Use our utility function as fallback
    updateTabTitle(pendingApprovalsCount);

    // 4. Log what the title actually is after updates
    console.log("Final document.title:", document.title);
  }, [pendingApprovalsCount]);

  // Use the hook for tab notifications with badge and sound
  useTabNotifications(pendingApprovalsCount, true, prevPendingCountRef);

  // Load stats - defined early so it can be used in the dependency array
  const loadStats = useCallback(async () => {
    try {
      // Fetch status counts
      const statusResult = await fetchBookingStatusCounts();
      console.log("Status counts fetched:", statusResult.data);

      if (!statusResult.error) {
        // Update the state with the fetched status counts
        setStatusCounts(statusResult.data);

        // Check if pending count increased
        const newPendingCount =
          statusResult.data.find((s) => s.status === "submitted")?.count || 0;
        const prevPendingCount = prevPendingCountRef.current;

        // Update title immediately for faster badge display
        if (newPendingCount > 0) {
          document.title = `(${newPendingCount}) Bookings Admin`;
          console.log(
            `Direct title update in loadStats: (${newPendingCount}) Bookings Admin`
          );

          // Also store in localStorage
          localStorage.setItem(
            "pendingApprovalCount",
            newPendingCount.toString()
          );

          // Use window method if available
          if (window.updateTitleWithCount) {
            window.updateTitleWithCount(newPendingCount);
          }
        }

        // Play sound and show notification ONLY if pending count actually increased
        if (newPendingCount > prevPendingCount) {
          console.log(
            `Pending approvals increased from ${prevPendingCount} to ${newPendingCount}`
          );
          playNotificationSound();

          // Also show a browser notification for new pending approvals
          showBrowserNotification(
            `You have ${newPendingCount} pending booking${
              newPendingCount !== 1 ? "s" : ""
            } that require approval`
          );
        }

        // Update reference for next comparison
        prevPendingCountRef.current = newPendingCount;
      } else {
        console.error("Error fetching status counts:", statusResult.error);
      }

      // Fetch total count
      const countResult = await fetchTotalBookingsCount();
      console.log("Total bookings count fetched:", countResult.count);

      if (!countResult.error) {
        setTotalBookingsCount(countResult.count);
      } else {
        console.error("Error fetching total count:", countResult.error);
      }
    } catch (error) {
      console.error("Error loading booking stats:", error);
      message.error("Failed to load booking statistics");
    }
  }, []);

  // Force refresh of bookings list - define this early so it can be referenced
  const forceBookingsRefresh = useCallback(() => {
    console.log("Forcing bookings list refresh");
    // Generate a unique timestamp that will definitely be different
    const refreshTimestamp = new Date();
    // Use a timeout to ensure state updates don't conflict
    setTimeout(() => {
      setLastUpdate(refreshTimestamp);
      console.log("Set lastUpdate to:", refreshTimestamp);
    }, 100);
  }, []);

  // Handle booking update from real-time subscription
  const handleBookingUpdate = useCallback(
    (booking: Booking) => {
      console.log("Handling booking update:", booking);

      // @ts-expect-error - is_refresh_only is a custom property we added
      const isRefreshOnly = booking.is_refresh_only === true;

      // Check if this is a special polling refresh (shouldn't affect counts)
      const isPollingRefresh = booking.status === "polling_refresh";

      // For polling refreshes, we only need to check for new pending bookings
      // We can optimize by doing a focused check for pending approvals only
      if (isPollingRefresh) {
        console.log(
          "Handling polling refresh - checking pending approvals only"
        );

        // Only refresh the pending approvals count, not all statuses
        // This is a more targeted approach that avoids unnecessary database queries
        const checkPendingApprovalsOnly = async () => {
          try {
            // Only fetch the count of pending approvals
            const { count, error } = await typedSupabase
              .from("bookings")
              .select("id", { count: "exact", head: true })
              .eq("status", "submitted");

            if (error) {
              console.error("Error checking pending approvals count:", error);
              return;
            }

            console.log(`Found ${count} pending approval bookings`);

            // Update the count if it's different from what we have
            const currentPendingCount =
              statusCounts.find((s) => s.status === "submitted")?.count || 0;

            if (count !== currentPendingCount) {
              console.log(
                `Pending count changed from ${currentPendingCount} to ${count}, refreshing data`
              );

              // Only update the submitted count, not all statuses
              const updatedStatusCounts = [...statusCounts];
              const submittedIndex = updatedStatusCounts.findIndex(
                (s) => s.status === "submitted"
              );

              if (submittedIndex >= 0) {
                // Update only the submitted count
                updatedStatusCounts[submittedIndex] = {
                  status: "submitted",
                  count: count || 0,
                };
                setStatusCounts(updatedStatusCounts);
              }

              // Force a refresh of the bookings list if we're on the pending tab
              if (activeTab === "pending") {
                forceBookingsRefresh();
              }
            }
          } catch (err) {
            console.error("Error in focused pending approvals check:", err);
          }
        };

        checkPendingApprovalsOnly();
        return;
      }

      // Check if this is a newly submitted booking
      const isNewPendingApproval = booking.status === "submitted";

      // For actual booking updates, we should still refresh all status counts
      // since the status of a booking might have changed
      loadStats();

      // Special approach for refreshing the table with a new Date object
      // This forces React to recognize the object as changed
      console.log("Forcing bookings list refresh after update");
      forceBookingsRefresh();

      // If the updated booking is the selected one, refresh its details
      if (selectedBooking && selectedBooking.id === booking.id) {
        // Directly fetch updated booking details instead of calling handleViewBookingDetails
        // This avoids the circular dependency
        fetchBookingById(booking.id)
          .then((result) => {
            if (!result.error && result.data) {
              setSelectedBooking(result.data);
            }
          })
          .catch((err) => {
            console.error("Error refreshing booking details:", err);
          });
      }

      // Only play notification sound for pending approvals if this is a real booking update
      // and not just a refresh from polling
      if (isNewPendingApproval && !isRefreshOnly) {
        console.log(
          "Playing notification sound for pending approval (real update, not refresh)"
        );
        playNotificationSound();

        // Only show browser notification for real bookings, not our sentinel value or refresh polls
        if (booking.id > 0) {
          showBrowserNotification("New booking requires your approval");
        }
      }
    },
    [selectedBooking, loadStats, forceBookingsRefresh, statusCounts, activeTab]
  );

  // Subscribe to real-time updates (now just polling)
  const { manualPoll, cancelRequests } = useBookingUpdates({
    onUpdate: handleBookingUpdate,
    activeTab: activeTab,
    pollOnlyWhenTabIs: "pending",
    // Disable realtime and force fallback mode
    enableRealtime: false,
    enableFallback: true,
    fallbackInterval: 30000, // Poll every 30 seconds (reduced from 2 minutes for easier testing)
  });

  // View booking details - defined early for dependency array
  const handleViewBookingDetails = useCallback(
    async (booking: Booking) => {
      try {
        // Cancel any pending polling requests when opening details
        if (activeTab === "pending") {
          cancelRequests();
        }

        // Always fetch full booking details to ensure we have fresh data
        const result = await fetchBookingById(booking.id);
        if (result.error) {
          message.error("Failed to load complete booking details");
          return;
        }
        setSelectedBooking(result.data);

        setViewDetailsModalVisible(true);
      } catch (error) {
        console.error("Error viewing booking details:", error);
        message.error("Failed to load booking details");
      }
    },
    [activeTab, cancelRequests]
  );

  // Effect to track usage and load stats
  useEffect(() => {
    // Track page view
    trackFeatureUsage("bookings_admin");

    // Fetch status counts and total count
    console.log("Initial loading of stats on component mount");
    loadStats();

    // Force a second load of stats after a delay to ensure counts are updated
    setTimeout(() => {
      console.log("Delayed reload of stats to ensure counts are correct");
      loadStats();
    }, 2000);

    // Force update the title immediately after component mounts
    // This ensures the badge appears on initial load
    setTimeout(() => {
      const pendingCount =
        statusCounts.find((s) => s.status === "submitted")?.count || 0;
      console.log("Initial title update with pending count:", pendingCount);
      updateTabTitle(pendingCount);

      // Also set the document.title directly for extreme reliability
      if (pendingCount > 0) {
        document.title = `(${pendingCount}) Bookings Admin`;
      }
    }, 1000); // Small delay to ensure counts are loaded
  }, [loadStats]);

  // Effect to log polling status
  useEffect(() => {
    console.log(
      `Polling ${
        activeTab === "pending" ? "active" : "paused"
      } (${activeTab} tab)`
    );
  }, [activeTab]);

  // Handle tab change
  const handleTabChange = (key: string) => {
    // If switching away from pending tab, cancel any in-flight requests
    if (activeTab === "pending" && key !== "pending") {
      console.log("Switching away from pending tab - canceling requests");
      cancelRequests();
    }

    setActiveTab(key);
    // Reset the status filter when changing tabs
    setSelectedStatusFilter(null);

    // If switching to pending tab, do an immediate poll
    if (key === "pending") {
      manualPoll();
      loadStats(); // Also refresh stats
      // Also force a list refresh to ensure we have the latest data
      forceBookingsRefresh();
    }
  };

  // Handle successful details update
  const handleUpdateSuccess = () => {
    // Refresh the booking list by updating the lastUpdate timestamp
    forceBookingsRefresh();

    // Refresh stats
    loadStats();
  };

  // Handle packing list generation and download
  const handleDownloadPackingSlip = async (booking: Booking) => {
    try {
      await generateAndDownloadPackingSlip(booking);
    } catch (error) {
      console.error("Error generating packing list:", error);
      message.error("Failed to generate packing list");
    }
  };

  // Handle status metric click
  const handleStatusMetricClick = (status: string) => {
    trackFeatureUsage("booking_status_metric_clicked", { status });
    setSelectedStatusFilter(status);

    // If we're on the all tab, we don't need to change it
    if (activeTab !== "all") {
      setActiveTab("all");
    }

    // Set flag to reset all other filters
    setResetFilters(new Date().getTime());
  };

  // Format status for display
  const formatStatusLabel = (status: string) => {
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Handle booking details modal cancel with unsaved changes check
  const handleModalClose = useCallback(() => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: "Unsaved Changes",
        content:
          "You have unsaved changes. Are you sure you want to close this dialog?",
        okText: "Yes, Close",
        cancelText: "No, Stay",
        onOk: () => {
          // Clear modal and reset selected booking to prevent state persistence between bookings
          setViewDetailsModalVisible(false);
          setSelectedBooking(null);
          setHasUnsavedChanges(false);

          // Resume polling if we're on the pending tab
          if (activeTab === "pending") {
            manualPoll();
          }
        },
      });
    } else {
      // No unsaved changes, close directly
      setViewDetailsModalVisible(false);
      setSelectedBooking(null);

      // Resume polling if we're on the pending tab
      if (activeTab === "pending") {
        manualPoll();
      }
    }
  }, [activeTab, hasUnsavedChanges, manualPoll]);

  // Effect to load booking from URL parameter if present
  useEffect(() => {
    const loadBookingFromUrl = async () => {
      // Check if booking_id parameter exists
      if (bookingIdParam) {
        try {
          const bookingId = parseInt(bookingIdParam);
          if (!isNaN(bookingId)) {
            console.log(
              `Loading booking details for ID ${bookingId} from URL parameter`
            );

            // Fetch booking details
            const result = await fetchBookingById(bookingId);
            if (!result.error && result.data) {
              // Set selected booking and open modal
              setSelectedBooking(result.data);
              setViewDetailsModalVisible(true);
            } else {
              console.error(
                "Error loading booking from URL parameter:",
                result.error
              );
              message.error(`Booking with ID ${bookingId} not found`);
            }
          }
        } catch (error) {
          console.error("Error loading booking from URL parameter:", error);
          message.error("Failed to load booking details");
        }
      }
    };

    loadBookingFromUrl();
  }, [bookingIdParam]); // Only run when bookingIdParam changes

  return (
    <Container>
      <AdminNavBar />

      <Title level={2}>
        Bookings Management
        {activeTab === "pending" ? (
          <Tag color="blue" style={{ marginLeft: 8, fontSize: 12 }}>
            Auto-refresh active (every 30s)
          </Tag>
        ) : (
          <Tooltip title="Switch to Pending Approval tab for auto-refresh">
            <Tag
              color="orange"
              style={{ marginLeft: 8, fontSize: 12, cursor: "pointer" }}
              onClick={() => {
                setActiveTab("pending");
                message.info(
                  "Switched to Pending Approval tab for auto-refresh"
                );
              }}
            >
              Auto-refresh paused
            </Tag>
          </Tooltip>
        )}
        <Tag
          color="green"
          style={{ marginLeft: 8, cursor: "pointer" }}
          onClick={() => {
            // Test sound function
            console.log("Testing notification sound...");
            playNotificationSound();
            message.success("Test notification sound played");
          }}
        >
          Test Sound
        </Tag>
      </Title>

      {/* Status Metrics */}
      <StatusMetricsContainer>
        {/* Debug logs for status counts */}
        {(() => {
          console.log("Rendering status metrics with counts:", statusCounts);
          const hasNonZeroCount = statusCounts.some((s) => s.count > 0);
          console.log("Has at least one non-zero count:", hasNonZeroCount);
          return null;
        })()}

        {/* Total bookings metric */}
        <StatusMetricCard
          key="total"
          onClick={() => {
            trackFeatureUsage("booking_total_metric_clicked");
            setSelectedStatusFilter("all");
            if (activeTab !== "all") {
              setActiveTab("all");
            }
            // Set flag to reset all other filters
            setResetFilters(new Date().getTime());
          }}
          style={{
            borderTop: `2px solid #1890ff`,
            background:
              selectedStatusFilter === "all"
                ? `rgba(24, 144, 255, 0.1)`
                : "white",
          }}
          bodyStyle={{ padding: "8px 12px" }}
        >
          <Statistic
            title="Total"
            value={totalBookingsCount || 0}
            valueStyle={{
              color: selectedStatusFilter === "all" ? "#1890ff" : "inherit",
              fontWeight: selectedStatusFilter === "all" ? "bold" : "normal",
            }}
          />
        </StatusMetricCard>

        {validStatuses.map((status) => {
          // Find the count for this status
          const statusData = statusCounts.find((s) => s.status === status);
          // Use the count if found, otherwise 0
          const count = statusData?.count || 0;

          const color = statusColors[status as keyof typeof statusColors];

          console.log(
            `Status: ${status}, Count: ${count}, Found in data: ${!!statusData}`
          );

          return (
            <StatusMetricCard
              key={status}
              onClick={() => handleStatusMetricClick(status)}
              style={{
                borderTop: `2px solid ${color}`,
                background:
                  selectedStatusFilter === status ? `${color}10` : "white",
              }}
              bodyStyle={{ padding: "8px 12px" }}
            >
              <Statistic
                title={formatStatusLabel(status)}
                value={count}
                valueStyle={{
                  color: selectedStatusFilter === status ? color : "inherit",
                  fontWeight:
                    selectedStatusFilter === status ? "bold" : "normal",
                }}
              />
            </StatusMetricCard>
          );
        })}
      </StatusMetricsContainer>

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        style={{ marginBottom: 16 }}
      >
        <TabPane
          tab={
            <span>
              Pending Approval
              {pendingApprovalsCount > 0 && (
                <Tag
                  color="red"
                  style={{
                    marginLeft: 8,
                    borderRadius: "10px",
                    fontSize: "12px",
                    lineHeight: "16px",
                    padding: "0 6px",
                  }}
                >
                  {pendingApprovalsCount}
                </Tag>
              )}
            </span>
          }
          key="pending"
        >
          <BookingsList
            isPendingList
            onViewDetails={handleViewBookingDetails}
            onRefresh={loadStats}
            lastUpdate={lastUpdate}
            statusFilter={selectedStatusFilter || "submitted"}
            resetFilters={resetFilters}
          />
        </TabPane>
        <TabPane tab="All Bookings" key="all">
          <BookingsList
            onViewDetails={handleViewBookingDetails}
            onRefresh={loadStats}
            lastUpdate={lastUpdate}
            statusFilter={selectedStatusFilter || "all"}
            resetFilters={resetFilters}
          />
        </TabPane>
      </Tabs>

      {/* Booking Details Modal */}
      <Modal
        title={
          selectedBooking
            ? `Booking Details - EFF${selectedBooking.id}${
                selectedBooking.transport_type_info
                  ? Array.isArray(selectedBooking.transport_type_info)
                    ? selectedBooking.transport_type_info[0]?.abbreviation || ""
                    : selectedBooking.transport_type_info?.abbreviation || ""
                  : ""
              } - ${
                selectedBooking.company_info
                  ? Array.isArray(selectedBooking.company_info)
                    ? selectedBooking.company_info[0]?.name
                    : selectedBooking.company_info?.name
                  : "Unknown Company"
              }`
            : "Booking Details"
        }
        open={viewDetailsModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={1200}
      >
        {selectedBooking && (
          <BookingDetails
            booking={selectedBooking}
            onEdit={() => {}}
            onDownloadPackingSlip={handleDownloadPackingSlip}
            onRefresh={async () => {
              // Refresh stats
              loadStats();

              // Update list trigger using our new robust method
              forceBookingsRefresh();

              // Refetch the selected booking to get updated data
              if (selectedBooking) {
                try {
                  const result = await fetchBookingById(selectedBooking.id);
                  if (!result.error && result.data) {
                    setSelectedBooking(result.data);
                  }
                } catch (error) {
                  console.error("Error refreshing booking details:", error);
                  // Continue execution - we don't want to block other refresh operations
                }
              }

              // Signal success for any other components
              handleUpdateSuccess();

              // Reset unsaved changes flag after refresh
              setHasUnsavedChanges(false);
            }}
            onChangesMade={(hasChanges) => setHasUnsavedChanges(hasChanges)}
            defaultTab={tabParam === "products" ? "3" : "1"}
          />
        )}
      </Modal>
    </Container>
  );
};

export default BookingsAdmin;
